import { Group } from "@/data/groups/Group";
import { NotificationsData } from "@/data/notifications/NotificationsData";
import { Organization } from "@/data/Organization";
import { CurrentUser } from "@/data/users/CurrentUser";
import { BaseSelectorOption } from "@/types";
import CloseIcon from "@mui/icons-material/Close";
import { SelectChangeEvent } from "@mui/material";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Toolbar from "@mui/material/Toolbar";
import { ChangeEvent, Component, SyntheticEvent } from "react";
import { AppContext } from "../common/AppContext";
import { Dashboard_mode } from "../constants";
import { withLocale, WithLocaleProps } from "../providers/LocaleProvider";
import { Button } from "./Button";
import { NotificationList } from "./NotificationList";
import { SelectMenu } from "./SelectMenu";

interface NotificationDrawerProps extends WithLocaleProps {
  width?: number;
  groups?: Group[] | null;
  onLoadNotifications?: (
    mode: (typeof Dashboard_mode)[keyof typeof Dashboard_mode],
    groupId?: string | null
  ) => void;
  onNotificationClick?: (e: React.MouseEvent, notif: any, groupId?: string | null) => void;
  onNavigation?: (e: React.MouseEvent, callback: ((e: React.MouseEvent) => void) | null) => void;
  organization?: Organization | null;
  user?: CurrentUser | null;
}

interface NotificationDrawerState {
  open: boolean;
  mode: (typeof Dashboard_mode)[keyof typeof Dashboard_mode];
  notifications: Record<string, NotificationsData>;
  groups?: Group[] | null;
  group?: Group | null;
}

class NotificationDrawerBase extends Component<NotificationDrawerProps, NotificationDrawerState> {
  static contextType = AppContext;

  static defaultProps = {
    width: 400
  };

  constructor(props: NotificationDrawerProps) {
    super(props);

    this.state = {
      open: false,
      mode: Dashboard_mode.personal,
      notifications: {},
      groups: props.groups,
      group: null
    };
  }

  componentDidUpdate(prevProps: NotificationDrawerProps, prevState: NotificationDrawerState) {
    if (prevProps.groups !== this.props.groups) this.setState({ groups: this.props.groups });
  }

  onClose() {
    this.close();
  }

  onModeChange(
    e: SyntheticEvent | null,
    mode: (typeof Dashboard_mode)[keyof typeof Dashboard_mode]
  ) {
    this.setState({ mode }, () => this.loadNotifications(mode));
  }

  loadNotifications(mode: (typeof Dashboard_mode)[keyof typeof Dashboard_mode], groupId?: string) {
    this.props.onLoadNotifications?.(mode, groupId);
  }

  componentDidMount() {
    this.loadNotifications(this.state.mode);
  }

  onNotificationReceived(notif: any, _numUnread?: number) {
    this.forceUpdate();
  }

  onNavigation(e: React.MouseEvent, callback: ((e: React.MouseEvent) => void) | null) {
    const { onNavigation } = this.props;
    return onNavigation && onNavigation(e, callback);
  }

  onNotificationClick(e: React.MouseEvent, notif: any) {
    this.close();

    const { onNotificationClick } = this.props;
    const { group } = this.state;

    if (onNotificationClick)
      this.onNavigation(e, (e: any) => onNotificationClick(e, notif, group ? group.groupId : null));
  }

  onGroupChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<BaseSelectorOption>
  ) {
    const { groups } = this.state;
    this.setState(
      {
        group: groups
          ? groups.find((g: any) => g.groupId === (e.target.value as BaseSelectorOption).id)
          : null
      },
      () =>
        this.loadNotifications(
          Dashboard_mode.groups,
          this.state.group ? this.state.group.groupId : undefined
        )
    );
  }

  open(mode: (typeof Dashboard_mode)[keyof typeof Dashboard_mode], open: boolean) {
    const modeChange = this.state.mode !== mode;
    this.setState({ open: open === true || open === undefined, mode }, () => {
      if (modeChange) this.onModeChange(null, mode);
    });
  }

  close() {
    this.setState({ open: false });
  }

  render() {
    const { organization, user, width, groups, translate } = this.props;
    const { open, mode, notifications, group } = this.state;

    const groupItems = [];
    if (groups) {
      groupItems.push({ label: translate("notifications.allGroups") });
      groupItems.push(...Object.values(groups).map((g: any) => ({ id: g.groupId, label: g.name })));
    }

    return (
      <Drawer
        open={open}
        className='klayo-notificationdrawer klayo-drawer'
        anchor='right'
        onClose={this.onClose.bind(this)}
        sx={{
          "& .MuiDrawer-paper": {
            overflow: "hidden"
          }
        }}
      >
        <Stack
          direction='column'
          sx={{
            width: {
              xs: "100vw",
              sm: width
            },
            height: "100%"
          }}
        >
          <div>
            <Toolbar
              className='klayo-notificationdrawer_toolbar'
              sx={{
                padding: {
                  xs: "24px 24px!important",
                  sm: "24px 24pxpx!important",
                  md: "24px 32px!important"
                }
              }}
            >
              <Box
                className='klayo-notificationdrawer_title'
                sx={{ flexGrow: 1, fontSize: { xs: "20px", sm: "20px", md: "24px" } }}
              >
                {translate("notifications.title")}
              </Box>
              <Button
                className='klayo-appdrawer_closebutton'
                autoFocus
                variant='simple'
                size='sm'
                icon={<CloseIcon />}
                onClick={this.onClose.bind(this)}
              />
            </Toolbar>

            {user && (user.isTeamLead || user.hasGroup) && (
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  className='klayo-notificationdrawer_tabs'
                  centered={true}
                  value={mode}
                  variant='fullWidth'
                  onChange={this.onModeChange.bind(this)}
                  sx={{
                    padding: {
                      xs: "0 24px!important",
                      sm: "0 24pxpx!important",
                      md: "0 32px!important"
                    },
                    width: "100%"
                  }}
                >
                  <Tab label={translate("notifications.tabs.me")} value={Dashboard_mode.personal} />
                  {user.isTeamLead && (
                    <Tab
                      label={translate("notifications.tabs.myTeam")}
                      value={Dashboard_mode.team}
                    />
                  )}
                  {user.hasGroup && (
                    <Tab
                      label={translate("notifications.tabs.myGroups")}
                      value={Dashboard_mode.groups}
                    />
                  )}
                </Tabs>
              </Box>
            )}
          </div>
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            {mode === Dashboard_mode.personal && (
              <Fade in={true}>
                <Box sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
                  <NotificationList
                    organization={organization}
                    user={user}
                    notifications={notifications[Dashboard_mode.personal.name]}
                    onNotificationClick={this.onNotificationClick.bind(this)}
                    translate={translate}
                  />
                </Box>
              </Fade>
            )}

            {user && user.isTeamLead && mode === Dashboard_mode.team && (
              <Fade in={true}>
                <Box sx={{ height: "100%", display: "flex", justifyContent: "center" }}>
                  <NotificationList
                    organization={organization}
                    user={user}
                    notifications={notifications[Dashboard_mode.team.name]}
                    onNotificationClick={this.onNotificationClick.bind(this)}
                    translate={translate}
                  />
                </Box>
              </Fade>
            )}

            {user && user.hasGroup && groups && mode === Dashboard_mode.groups && (
              <Fade in={true}>
                <Stack direction='column' sx={{ height: "100%" }}>
                  <div style={{ padding: "16px" }}>
                    <SelectMenu<BaseSelectorOption>
                      value={group ? groupItems.find((g: any) => g.id === group.groupId) : null}
                      firstValueDefault={true}
                      dense={true}
                      items={Object.entries(groupItems)}
                      clearable={false}
                      highlightSelection={false}
                      onChange={this.onGroupChange.bind(this)}
                      sx={{
                        width: "100%"
                      }}
                    />
                  </div>
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflow: "auto",
                      display: "flex",
                      justifyContent: "center"
                    }}
                  >
                    <NotificationList
                      organization={organization}
                      user={user}
                      notifications={notifications[Dashboard_mode.groups.name]}
                      group={group}
                      onNotificationClick={this.onNotificationClick.bind(this)}
                      translate={translate}
                    />
                  </Box>
                </Stack>
              </Fade>
            )}
          </Box>
        </Stack>
      </Drawer>
    );
  }
}

export const NotificationDrawer = withLocale(NotificationDrawerBase);
